/**
 * OutfitOfTheDayService — Agent de recommandation "Tenue du Jour" (Mytenue).
 *
 * Implémentation pure JavaScript (déterministe, pas d'appel IA payant) du spec
 * agent décrit dans le système. Fournit :
 *
 *   • scoreLook(look, prefs, context, recentLookIds) → { total, breakdown, penalties, eliminated }
 *   • rankLooks(looks, prefs, context, recentLookIds) → liste triée DESC
 *   • getOutfitOfTheDay(input) → JSON complet (outfit_of_the_day + alternatives + insights)
 *   • explainRecommendation(look, breakdown, prefs, context) → { explanation, reasons[] }
 *
 * Scope : looks IA (OutfitEngine sur dressing) + Mes Looks (userCreatedLooks).
 * Aucun outfit du catalogue partenaire — uniquement des vêtements possédés.
 *
 * v1.12.224
 */

// ─── Constantes pondération (spec v2 : 380 points max) ──────────────────
const W = {
  STYLE:     60,
  OCCASION:  60,
  CATEGORY:  40,
  WEATHER:   50,
  SEASON:    30,
  COLOR:     30,
  BRAND:     20,
  DIVERSITY: 20,
  COHERENCE: 30,
  BUDGET:    30, // v2
  MOOD:      10, // v2
};

const PENALTIES = {
  REPETITION_3D:    -50, // v2 : 3 derniers jours
  REPETITION_7D:    -30, // 4-7 jours en arrière
  COLOR_EXCLUDED:  -100, // v2 : élimination
  WRONG_WEATHER:    -40,
  WRONG_SEASON:     -20,
  OVER_BUDGET_HARD:-100, // v2 : >10% au-dessus → élimination
  OVER_BUDGET_SOFT: -10, // 0-10% au-dessus
  GENDER_MISMATCH: -100, // v2 : élimination
};

const MIN_ACCEPTABLE_SCORE = 100;
const STYLIST_MIN_SCORE    = 80;  // v2 : seuil moindre pour top N

// Mapping legacy budget string → { min, max }
const BUDGET_STR_TO_RANGE = {
  budget:      { min: 0,   max: 50 },
  'mid-range': { min: 50,  max: 150 },
  premium:     { min: 100, max: 500 },
  luxury:      { min: 500, max: 99999 },
};

const LUXURY_BRANDS = new Set([
  'hermes', 'hermès', 'chanel', 'dior', 'louis vuitton', 'lv', 'gucci', 'prada',
  'saint laurent', 'ysl', 'balenciaga', 'bottega veneta', 'givenchy', 'valentino',
  'loewe', 'celine', 'céline', 'fendi', 'versace', 'goyard', 'cartier', 'rolex',
]);

function normalizeBudget(prefs) {
  if (!prefs) return null;
  // 1) Objet déjà au bon format
  if (prefs.budget && typeof prefs.budget === 'object' && (prefs.budget.min != null || prefs.budget.max != null)) {
    return {
      min: Number(prefs.budget.min) || 0,
      max: prefs.budget.max != null ? Number(prefs.budget.max) : 99999,
      flexibility: prefs.budget.flexibility != null ? Number(prefs.budget.flexibility) : 0.1,
    };
  }
  // 2) prefs.budget string
  if (typeof prefs.budget === 'string') {
    const r = BUDGET_STR_TO_RANGE[prefs.budget];
    if (r) return { ...r, flexibility: 0.1 };
  }
  // 3) prefs.budget_max plain
  if (typeof prefs.budget_max === 'number') {
    return { min: 0, max: prefs.budget_max, flexibility: prefs.flexibility ?? 0.1 };
  }
  return null;
}

function hasLuxuryBrandInPrefs(prefs) {
  const brands = (prefs?.brands || []).map(lc);
  return brands.some(b => LUXURY_BRANDS.has(b) || Array.from(LUXURY_BRANDS).some(lx => b.includes(lx) || lx.includes(b)));
}

// ─── Helpers généraux ───────────────────────────────────────────────────
function lc(s) { return String(s || '').toLowerCase().trim(); }

function intersectCount(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) return 0;
  const set = new Set(a.map(lc));
  let n = 0;
  for (const x of b) if (set.has(lc(x))) n++;
  return n;
}

function dominantColors(look) {
  // Retourne tableau lowercased des couleurs des items
  const cols = (look.items || [])
    .map(it => it?.color || it?.ai_metadata?.primaryColor || '')
    .map(lc)
    .filter(Boolean);
  return Array.from(new Set(cols));
}

function lookCategorySet(look) {
  return new Set((look.items || []).map(it => lc(it?.category)).filter(Boolean));
}

function totalLookPrice(look) {
  return (look.items || []).reduce((s, it) => s + (Number(it?.price) || 0), 0);
}

// Détecte la saison à partir d'une date (FR / hémisphère nord)
export function detectSeason(date = new Date()) {
  const m = date.getMonth(); // 0=jan
  if (m <= 1 || m === 11) return 'hiver';
  if (m <= 4) return 'printemps';
  if (m <= 7) return 'été';
  return 'automne';
}

const SEASON_ADJACENT = {
  printemps: ['été', 'automne'],
  été:       ['printemps'],
  automne:   ['printemps', 'hiver'],
  hiver:     ['automne'],
};

// ─── Scoring : critères individuels ─────────────────────────────────────
function scoreStyle(look, prefs) {
  const userStyles = (prefs.styles || []).map(lc);
  if (userStyles.length === 0) return { score: 0, hits: [] };
  const lookStyles = (look.style_tags || []).map(lc);
  const matches = userStyles.filter(s => lookStyles.includes(s));
  if (matches.length === 0) {
    // match partiel via inclusion (smart_casual ↔ casual)
    const partial = userStyles.some(s =>
      lookStyles.some(t => t.includes(s) || s.includes(t))
    );
    return { score: partial ? 20 : 0, hits: partial ? ['partial'] : [] };
  }
  const perStyle = W.STYLE / userStyles.length;
  let score = matches.length * perStyle;
  if (matches.length >= 2) score += 10;
  return { score: Math.min(W.STYLE + 10, score), hits: matches };
}

function scoreOccasion(look, prefs) {
  // Spec utilise prefs.occasions (array) ; on supporte aussi prefs.occasion (string)
  const userOccs = []
    .concat(prefs.occasions || [])
    .concat(prefs.occasion ? [prefs.occasion] : [])
    .map(lc).filter(Boolean);
  if (userOccs.length === 0) return { score: 0, hits: [] };
  const lookOccs = (look.occasion_tags || []).map(lc);
  if (lookOccs.length === 0) return { score: 0, hits: [] };
  const exact = userOccs.filter(o => lookOccs.includes(o));
  if (exact.length > 0) {
    return { score: Math.min(W.OCCASION, exact.length * (W.OCCASION / userOccs.length)), hits: exact };
  }
  // Match partiel (substring fuzzy : "casual" ↔ "smart_casual")
  const partial = userOccs.some(u =>
    lookOccs.some(l => l.includes(u) || u.includes(l))
  );
  return { score: partial ? 40 : 0, hits: partial ? ['partial'] : [] };
}

function scoreCategory(look, prefs) {
  const userCats = (prefs.categories || []).map(lc);
  if (userCats.length === 0) return { score: 0, hits: [] };
  const cats = lookCategorySet(look);
  const hits = [];
  // Mots-clés "structuraux" attendus dans prefs.categories
  // top+bottom = haut + bas dans look
  // dress = robe
  // robe = robe
  // manteau = manteau
  // veste = veste
  for (const c of userCats) {
    if (c === 'dress' || c === 'robe' || c === 'robes') {
      if (cats.has('robes')) hits.push('robes');
    } else if (c === 'top+bottom' || c === 'haut+bas' || c === 'haut_bas') {
      if (cats.has('hauts') && cats.has('bas')) hits.push('haut+bas');
    } else if (c === 'manteau' || c === 'manteaux') {
      if (cats.has('manteaux')) hits.push('manteaux');
    } else if (c === 'veste' || c === 'vestes') {
      if (cats.has('vestes')) hits.push('vestes');
    } else if (cats.has(c)) {
      hits.push(c);
    }
  }
  if (hits.length === 0) return { score: 0, hits: [] };
  return { score: W.CATEGORY, hits }; // catégorie matchée → full points
}

function scoreWeather(look, context) {
  const w = context?.weather || {};
  const t = typeof w.temp_celsius === 'number' ? w.temp_celsius : (typeof context?.tempC === 'number' ? context.tempC : null);
  if (t == null) return { score: 25, breakdown: 'no_temp_data' };

  const cats = lookCategorySet(look);
  const hasManteau = cats.has('manteaux');
  const hasVeste   = cats.has('vestes');
  const hasShorts  = (look.items || []).some(it =>
    /\b(short|shorts|bermuda|d[eé]bardeur|tank)\b/i.test(it?.name || '') || /\b(short|shorts|bermuda)\b/i.test(it?.subcategory || '')
  );

  let score = 25;
  let breakdown = '';

  if (t < 10) {
    if (hasManteau) { score = W.WEATHER; breakdown = 'manteau_for_cold'; }
    else if (hasVeste) { score = 20; breakdown = 'only_veste_for_cold'; }
    else { score = 0; breakdown = 'no_warm_layer'; }
  } else if (t < 15) {
    if (hasManteau || hasVeste) { score = W.WEATHER; breakdown = 'layer_for_cool'; }
    else { score = 25; breakdown = 'no_layer_for_cool'; }
  } else if (t < 20) {
    if (hasVeste && !hasManteau) { score = W.WEATHER; breakdown = 'cardigan_for_mild'; }
    else if (hasManteau) { score = 20; breakdown = 'too_warm_manteau'; }
    else { score = 35; breakdown = 'mild_no_layer'; }
  } else if (t < 25) {
    if (!hasManteau) { score = W.WEATHER; breakdown = 'light_for_warm'; }
    else { score = 10; breakdown = 'manteau_too_warm'; }
  } else {
    if (!hasManteau && !hasVeste) { score = W.WEATHER; breakdown = 'light_for_hot'; }
    else if (hasShorts) { score = 35; breakdown = 'shorts_helps'; }
    else { score = 0; breakdown = 'too_warm_layers'; }
  }

  // Bonus/malus météo conditions
  const cond = lc(w.condition);
  if (/pluie|rain|shower/.test(cond)) {
    const hasSuede = (look.items || []).some(it =>
      /\b(daim|su[eè]de|suede)\b/i.test(`${it?.material || ''} ${it?.name || ''}`)
    );
    if (hasSuede) score -= 20;
  }
  if (/neige|snow/.test(cond)) {
    const hasBoots = (look.items || []).some(it =>
      /\b(bottes?|boots?|bottines?)\b/i.test(it?.name || '') ||
      lc(it?.subcategory).includes('bottes') ||
      lc(it?.subcategory).includes('boots')
    );
    if (hasBoots) score += 20;
  }
  if (/vent|wind/.test(cond)) {
    const hasLightSkirt = (look.items || []).some(it =>
      /\bjupes?\s+(volant|l[eé]g[eè]re|fluide|plisse|midi)/i.test(it?.name || '')
    );
    if (hasLightSkirt) score -= 10;
  }

  return { score: Math.max(0, Math.min(W.WEATHER, score)), breakdown };
}

function scoreSeason(look, context) {
  const season = lc(context?.season || detectSeason(new Date(context?.date || Date.now())));
  if (!season) return { score: 0 };
  const tags = (look.season_tags || []).map(lc);
  if (tags.length === 0) return { score: 15 }; // neutre si pas de tags
  if (tags.includes(season) || tags.includes('toute_saison') || tags.includes('all_season')) {
    return { score: W.SEASON, hit: 'exact' };
  }
  const adj = SEASON_ADJACENT[season] || [];
  if (tags.some(t => adj.includes(t))) {
    return { score: 15, hit: 'adjacent' };
  }
  // Saison opposée → pénalité (handled séparément dans penalties)
  return { score: 0, hit: 'opposite' };
}

function scoreColor(look, prefs) {
  const userColors   = (prefs.colors || []).map(lc);
  const excludedCols = (prefs.excluded_colors || []).map(lc);
  const cols = dominantColors(look);
  let score = 0;
  let eliminated = false;
  const hits = [];
  for (const c of cols) {
    if (excludedCols.some(ex => c.includes(ex) || ex.includes(c))) {
      eliminated = true; // déclenche la pénalité hard
      break;
    }
    if (userColors.some(uc => c.includes(uc) || uc.includes(c))) {
      score += 10;
      hits.push(c);
    }
  }
  if (eliminated) return { score: 0, eliminated: true, hits: [] };
  return { score: Math.min(W.COLOR, score), eliminated: false, hits };
}

function scoreBrand(look, prefs) {
  const userBrands = (prefs.brands || []).map(lc);
  if (userBrands.length === 0) return { score: 0, hits: [] };
  const hits = [];
  let score = 0;
  for (const it of (look.items || [])) {
    const b = lc(it?.brand);
    if (b && userBrands.some(ub => b.includes(ub) || ub.includes(b))) {
      score += 10;
      hits.push(it.brand);
      if (score >= W.BRAND) break;
    }
  }
  return { score: Math.min(W.BRAND, score), hits };
}

function scoreDiversity(look, recentLookIds) {
  const ids = new Set((recentLookIds || []).map(String));
  if (ids.has(String(look.id))) return { score: 0, recent: true };
  let score = W.DIVERSITY;
  if (look.times_worn === 0 || look.times_worn == null) score += 0; // bonus géré dans insights
  return { score: Math.min(W.DIVERSITY, score), recent: false };
}

// v2 — BUDGET MATCH
function scoreBudget(look, prefs) {
  const range = normalizeBudget(prefs);
  if (!range) return { score: 0, status: 'no_budget', eliminated: false };
  const price = totalLookPrice(look);
  if (!(price > 0)) return { score: 0, status: 'no_price', eliminated: false };

  // Tolérance luxe : si user a sélectionné une marque luxe → x2
  const effMax = hasLuxuryBrandInPrefs(prefs) ? range.max * 2 : range.max;
  const flex = range.flexibility != null ? range.flexibility : 0.1;
  const hardLimit = effMax * (1 + flex);

  if (price > hardLimit) {
    return { score: 0, status: 'over_budget_hard', eliminated: true, price, hardLimit };
  }

  let score = 0;
  let status = 'in_budget';
  if (price <= effMax * 0.5)      { score = 30; status = 'great_value'; }
  else if (price <= effMax * 0.7) { score = 25; status = 'good_value'; }
  else if (price <= effMax)       { score = 20; status = 'in_budget'; }
  else /* price <= hardLimit */   { score = 0;  status = 'over_budget_soft'; }

  // Bonus pile dans la cible
  const target = ((range.min || 0) + effMax) / 2;
  if (target > 0 && Math.abs(price - target) <= target * 0.2) score = Math.min(W.BUDGET, score + 5);
  return { score, status, eliminated: false, price, range: { ...range, max: effMax } };
}

// v2 — MOOD MATCH (10 pts)
const MOOD_TAGS = {
  professional: ['workwear', 'bureau', 'travail', 'chic', 'minimaliste'],
  casual:       ['casual', 'streetwear', 'sport'],
  romantic:     ['romantique', 'chic', 'soirée'],
  cozy:         ['casual', 'minimaliste'],
  bold:         ['streetwear', 'soirée'],
  elegant:      ['chic', 'workwear'],
  sporty:       ['sport', 'streetwear'],
};
function scoreMood(look, context) {
  const mood = lc(context?.today_mood || context?.mood);
  if (!mood) return { score: 0, hit: null };
  const lookTags = (look.style_tags || []).concat(look.occasion_tags || []).map(lc);
  const expected = (MOOD_TAGS[mood] || []).map(lc);
  if (expected.length === 0) return { score: 0, hit: null };
  if (expected.some(t => lookTags.includes(t))) return { score: W.MOOD, hit: mood };
  return { score: 0, hit: null };
}

function scoreCoherence(look) {
  const c = typeof look.coherence_score === 'number'
    ? look.coherence_score
    : (typeof look.cohesion?.total === 'number'
        ? look.cohesion.total
        : (typeof look.score === 'number' ? Math.min(100, look.score) : null));
  if (c == null) return { score: 15 }; // neutre
  return { score: Math.round((Math.max(0, Math.min(100, c)) / 100) * W.COHERENCE) };
}

// ─── SCORE_LOOK : entrée principale ─────────────────────────────────────
export function scoreLook(look, prefs = {}, context = {}, recentLookIds = []) {
  if (!look) return { total: 0, breakdown: {}, penalties: 0, eliminated: true, reason: 'no_look' };

  // Gender check (élimination)
  const userGender = prefs.gender || prefs.user_gender || null;
  if (userGender && userGender !== 'N' && look.gender && look.gender !== 'unisex' && look.gender !== userGender) {
    return {
      total: 0, eliminated: true, reason: 'gender_mismatch',
      breakdown: { penalties: PENALTIES.GENDER_MISMATCH },
      penalties: PENALTIES.GENDER_MISMATCH,
    };
  }

  const style    = scoreStyle(look, prefs);
  const occasion = scoreOccasion(look, prefs);
  const category = scoreCategory(look, prefs);
  const weather  = scoreWeather(look, context);
  const season   = scoreSeason(look, context);
  const color    = scoreColor(look, prefs);
  const brand    = scoreBrand(look, prefs);
  const diversity= scoreDiversity(look, recentLookIds);
  const coherence= scoreCoherence(look);
  const budget   = scoreBudget(look, prefs);
  const mood     = scoreMood(look, context);

  // Éliminations immédiates
  if (color.eliminated) {
    return {
      total: 0, eliminated: true, reason: 'color_excluded',
      breakdown: { color: 0, penalties: PENALTIES.COLOR_EXCLUDED },
      penalties: PENALTIES.COLOR_EXCLUDED,
    };
  }
  if (budget.eliminated) {
    return {
      total: 0, eliminated: true, reason: 'over_budget',
      breakdown: { budget: 0, penalties: PENALTIES.OVER_BUDGET_HARD },
      penalties: PENALTIES.OVER_BUDGET_HARD,
      price: budget.price,
      hardLimit: budget.hardLimit,
    };
  }

  // Pénalités douces v2
  let penalties = 0;
  if (diversity.recent) {
    // diversity.recencyDays s'il existe, sinon -50 (worst case 3j)
    penalties += (diversity.recencyDays != null && diversity.recencyDays > 3)
      ? PENALTIES.REPETITION_7D
      : PENALTIES.REPETITION_3D;
  }
  if (season.hit === 'opposite') penalties += PENALTIES.WRONG_SEASON;
  if (weather.breakdown === 'no_warm_layer' || weather.breakdown === 'too_warm_layers') {
    penalties += PENALTIES.WRONG_WEATHER;
  }
  if (budget.status === 'over_budget_soft') penalties += PENALTIES.OVER_BUDGET_SOFT;

  const total = Math.max(0,
    style.score + occasion.score + category.score + weather.score + season.score +
    color.score + brand.score + diversity.score + coherence.score + budget.score + mood.score + penalties
  );

  return {
    total: Math.round(total),
    eliminated: false,
    breakdown: {
      style: Math.round(style.score),
      occasion: Math.round(occasion.score),
      category: Math.round(category.score),
      weather: Math.round(weather.score),
      season: Math.round(season.score),
      color: Math.round(color.score),
      brand: Math.round(brand.score),
      diversity: Math.round(diversity.score),
      coherence: Math.round(coherence.score),
      budget: Math.round(budget.score),
      mood: Math.round(mood.score),
      penalties,
    },
    matches: {
      style: style.hits || [],
      occasion: occasion.hits || [],
      category: category.hits || [],
      color: color.hits || [],
      brand: brand.hits || [],
      season: season.hit || null,
      weather: weather.breakdown || null,
      diversity: diversity.recent ? 'recent' : 'fresh',
      budget: budget.status,
      mood: mood.hit,
    },
    penalties,
    price: budget.price || 0,
    budget_status: budget.status,
  };
}

// ─── RANK_LOOKS ─────────────────────────────────────────────────────────
export function rankLooks(looks, prefs = {}, context = {}, recentLookIds = []) {
  if (!Array.isArray(looks) || looks.length === 0) return [];
  return looks
    .map(l => ({ look: l, scoring: scoreLook(l, prefs, context, recentLookIds) }))
    .filter(x => !x.scoring.eliminated)
    .sort((a, b) => b.scoring.total - a.scoring.total);
}

// ─── EXPLAIN_RECOMMENDATION ─────────────────────────────────────────────
const STYLE_ICONS = {
  chic: '✨', casual: '👕', romantique: '🌹', bohème: '🌾', sport: '🏃',
  streetwear: '🧢', minimaliste: '⚪', workwear: '💼', smart_casual: '✨', casual_chic: '✨',
};
const OCCASION_ICONS = {
  bureau: '💼', travail: '💼', soirée: '🥂', soiree: '🥂', weekend: '☕',
  date: '💕', sport: '🏋️', vacances: '🏖️', cérémonie: '👰', ceremonie: '👰',
  quotidien: '🚶', brunch: '☕', casual_friday: '🎉',
};

function dayOfWeekFR(date) {
  const days = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
  return days[(date instanceof Date ? date : new Date(date || Date.now())).getDay()] || 'aujourd\'hui';
}

function weatherSentence(t, cond) {
  if (t == null) return null;
  const c = lc(cond);
  if (/pluie|rain/.test(c)) return `temps pluvieux (${t}°C)`;
  if (/neige|snow/.test(c)) return `temps neigeux (${t}°C)`;
  if (/soleil|ensoleill|sunny/.test(c)) return `temps ensoleillé (${t}°C)`;
  if (/nuage|cloud/.test(c)) return `temps nuageux (${t}°C)`;
  return `${t}°C`;
}

export function explainRecommendation(look, scoring, prefs = {}, context = {}) {
  const reasons = [];
  const m = scoring?.matches || {};
  const w = context?.weather || {};
  const t = typeof w.temp_celsius === 'number' ? w.temp_celsius : (typeof context?.tempC === 'number' ? context.tempC : null);
  const day = dayOfWeekFR(context?.date);
  const season = lc(context?.season || detectSeason(new Date(context?.date || Date.now())));

  // v1.12.267 — Lang-aware via getCurrentLang()
  let _lang = 'fr';
  try { const { getCurrentLang } = require('../i18n'); _lang = getCurrentLang() || 'fr'; } catch {}
  const en = _lang === 'en';
  const seasonEN = { printemps: 'spring', été: 'summer', automne: 'autumn', hiver: 'winter' }[season] || season;

  // STYLE
  if (m.style && m.style.length > 0 && m.style[0] !== 'partial') {
    const s = m.style[0];
    const ico = STYLE_ICONS[s] || '✨';
    reasons.push(en ? `${ico} "${s}" style you love` : `${ico} Style "${s}" que vous adorez`);
  }
  // OCCASION
  if (m.occasion && m.occasion.length > 0 && m.occasion[0] !== 'partial') {
    const o = m.occasion[0];
    const ico = OCCASION_ICONS[o] || '✨';
    if (en) {
      reasons.push(`${ico} Perfect for ${o === 'travail' || o === 'bureau' ? 'work' : `your ${o}`}`);
    } else {
      reasons.push(`${ico} Parfait pour ${o === 'travail' || o === 'bureau' ? 'le travail' : `votre ${o}`}`);
    }
  }
  // CATEGORY
  if (m.category && m.category.length > 0) {
    const c = m.category[0];
    const ico = c === 'robes' ? '👗' : c === 'haut+bas' ? '👚' : c === 'manteaux' ? '🧥' : '🛍️';
    if (en) {
      reasons.push(`${ico} ${c === 'robes' ? 'Dress' : c === 'haut+bas' ? 'Top + bottom combo' : c === 'manteaux' ? 'Coat' : `Category ${c}`} in your preferences`);
    } else {
      reasons.push(`${ico} ${c === 'robes' ? 'Robe' : c === 'haut+bas' ? 'Combo haut + bas' : c === 'manteaux' ? 'Manteau' : `Catégorie ${c}`} dans vos préférences`);
    }
  }
  // WEATHER
  if (t != null && (scoring?.breakdown?.weather || 0) >= 35) {
    const wstr = weatherSentence(t, w.condition);
    reasons.push(en ? `🌤️ Weather-appropriate (${wstr})` : `🌤️ Adapté à la météo (${wstr})`);
  }
  // SEASON
  if (m.season === 'exact') {
    const ico = { printemps: '🌿', été: '☀️', automne: '🍂', hiver: '❄️' }[season] || '📅';
    reasons.push(en ? `${ico} Ideal for ${seasonEN}` : `${ico} Saison ${season} idéale`);
  }
  // COLOR
  if (m.color && m.color.length > 0) {
    reasons.push(en ? `🎨 ${m.color.slice(0, 2).join(' / ')} colors in your preferences` : `🎨 Couleurs ${m.color.slice(0, 2).join(' / ')} dans vos préférences`);
  }
  // BRAND
  if (m.brand && m.brand.length > 0) {
    reasons.push(en ? `🏷️ ${m.brand[0]} brand you follow` : `🏷️ Marque ${m.brand[0]} que vous suivez`);
  }
  // DIVERSITY
  if (m.diversity === 'fresh') {
    reasons.push(en ? `📅 You haven't worn this look recently` : `📅 Vous n'avez pas porté ce look récemment`);
  }

  // EXPLANATION (paragraphe)
  const lookName = look?.name ? `"${look.name}"` : 'cette tenue';
  const dayPhrase = day ? `ce ${day}` : 'aujourd\'hui';
  const seasonPhrase = season ? ` de ${season}` : '';
  const weatherPhrase = (t != null) ? `, et la température (${t}°C) est idéale` : '';
  const stylePhrase = (m.style && m.style.length > 0 && m.style[0] !== 'partial')
    ? ` au style ${m.style.slice(0, 2).join(' et ')}`
    : '';
  const occPhrase = (m.occasion && m.occasion.length > 0 && m.occasion[0] !== 'partial')
    ? ` pour ${m.occasion[0] === 'travail' || m.occasion[0] === 'bureau' ? 'le travail' : `votre ${m.occasion[0]}`}`
    : '';

  const explanation = `Cette tenue${stylePhrase} ${lookName} est idéale ${dayPhrase}${seasonPhrase}${occPhrase}${weatherPhrase}.`;

  return { explanation, reasons };
}

// ─── INSIGHTS GLOBAUX ───────────────────────────────────────────────────
function buildInsights(ranked, prefs, context) {
  const insights = [];
  const total = ranked.length;
  const styleHits = ranked.filter(r => (r.scoring.matches?.style || []).length > 0).length;
  let _l = 'fr';
  try { const { getCurrentLang } = require('../i18n'); _l = getCurrentLang() || 'fr'; } catch {}
  const en = _l === 'en';
  if (total > 0 && styleHits > 0) {
    if (en) {
      insights.push(`💡 You have ${styleHits} outfit${styleHits > 1 ? 's' : ''} matching your style`);
    } else {
      insights.push(`💡 Vous avez ${styleHits} tenue${styleHits > 1 ? 's' : ''} qui matche${styleHits > 1 ? 'nt' : ''} votre style`);
    }
  }
  const season = lc(context?.season || detectSeason(new Date(context?.date || Date.now())));
  if (season === 'printemps') insights.push(en ? '🌿 Spring season: think about adding lighter colors' : '🌿 Saison printemps : pensez à ajouter des couleurs claires');
  if (season === 'été')       insights.push(en ? '☀️ Summer season: favor light, breathable fabrics'   : '☀️ Saison été : privilégiez les tissus légers et clairs');
  if (season === 'automne')   insights.push(en ? '🍂 Autumn season: time to layer'                       : '🍂 Saison automne : c\'est le moment des superpositions');
  if (season === 'hiver')     insights.push(en ? '❄️ Winter season: make sure you have a coat in your wardrobe' : '❄️ Saison hiver : assurez-vous d\'avoir un manteau dans votre dressing');

  // Catégorie sous-utilisée
  const allCats = ranked.flatMap(r => Array.from(lookCategorySet(r.look)));
  const robesCount = allCats.filter(c => c === 'robes').length;
  if (robesCount > 0 && robesCount < ranked.length / 4) {
    insights.push(en ? '⭐ The dresses category is underused in your outfits' : '⭐ La catégorie robes est sous-utilisée dans vos tenues');
  }
  return insights;
}

// ─── GET_OUTFIT_OF_THE_DAY (entrée publique principale) ─────────────────
export function getOutfitOfTheDay(input) {
  const data = input?.data || {};
  const prefs = data.preferences || {};
  const context = data.context || {};
  const recentIds = Array.isArray(data.recently_worn_look_ids) ? data.recently_worn_look_ids : [];
  const looks = Array.isArray(data.available_looks) ? data.available_looks : [];

  if (looks.length === 0) {
    return {
      success: false,
      action: 'GET_OUTFIT_OF_THE_DAY',
      data: null,
      errors: [{
        code: 'NO_LOOKS_AVAILABLE',
        message: 'Aucun look disponible. Veuillez ajouter des items à votre dressing ou créer des looks.',
      }],
      warnings: [],
    };
  }

  // Filtre genre (utilisateur N = pas de filtre)
  const userGender = data.user_gender || prefs.gender || null;
  const filtered = userGender && userGender !== 'N'
    ? looks.filter(l => !l.gender || l.gender === 'unisex' || l.gender === userGender)
    : looks;

  const ranked = rankLooks(filtered, prefs, context, recentIds);

  if (ranked.length === 0) {
    return {
      success: true,
      action: 'GET_OUTFIT_OF_THE_DAY',
      data: {
        outfit_of_the_day: null,
        no_good_match: true,
        suggestions: [
          'Vos préférences sont strictes. Voulez-vous élargir vos critères ?',
          'Essayez d\'ajouter de nouveaux items à votre dressing',
          'Demandez à l\'IA de générer un look adapté à vos préférences',
        ],
      },
      errors: [{ code: 'NO_GOOD_MATCH', message: 'Aucun look ne correspond suffisamment à vos préférences (toutes éliminations).' }],
      warnings: [],
    };
  }

  const best = ranked[0];
  const altRaw = ranked.slice(1, 4);

  // Score minimum acceptable
  if ((best.scoring.total || 0) < MIN_ACCEPTABLE_SCORE) {
    const missing = [];
    const bd = best.scoring.breakdown || {};
    if ((bd.style || 0) < 20) missing.push('style');
    if ((bd.occasion || 0) < 20) missing.push('occasion');
    if ((bd.weather || 0) < 20) missing.push('weather');
    return {
      success: true,
      action: 'GET_OUTFIT_OF_THE_DAY',
      data: {
        outfit_of_the_day: null,
        no_good_match: true,
        best_available: {
          look_id: best.look.id,
          score: best.scoring.total,
          missing_criteria: missing,
        },
        suggestions: [
          'Vos préférences sont strictes. Voulez-vous élargir vos critères ?',
          'Essayez d\'ajouter de nouveaux items à votre dressing',
        ],
      },
      errors: [],
      warnings: [],
    };
  }

  const expl = explainRecommendation(best.look, best.scoring, prefs, context);

  // Alternatives : reason_short basé sur ce qui manque vs best
  const alternatives = altRaw.map(({ look, scoring }) => {
    const diff = (best.scoring.total || 0) - (scoring.total || 0);
    let reason = `Score ${scoring.total} (vs ${best.scoring.total}).`;
    const bd = scoring.breakdown || {};
    if ((bd.weather || 0) < (best.scoring.breakdown?.weather || 0)) reason = 'Moins adapté à la météo';
    else if ((bd.occasion || 0) < (best.scoring.breakdown?.occasion || 0)) reason = `Moins adapté à l'occasion`;
    else if ((bd.style || 0) < (best.scoring.breakdown?.style || 0)) reason = 'Style moins aligné';
    else if ((scoring.matches?.diversity) === 'recent') reason = 'Déjà porté récemment';
    return {
      look_id: look.id,
      name: look.name || `Look #${String(look.id).slice(-4)}`,
      score: scoring.total,
      reason_short: reason,
    };
  });

  // Warnings : préférences contradictoires
  const warnings = [];
  const userStyles = (prefs.styles || []).map(lc);
  const userOccs = ((prefs.occasions || []).concat(prefs.occasion ? [prefs.occasion] : [])).map(lc);
  if (userStyles.includes('sport') && userOccs.includes('soirée')) {
    warnings.push("Préférences contradictoires : style 'sport' et occasion 'soirée' rarement compatibles");
  }

  return {
    success: true,
    action: 'GET_OUTFIT_OF_THE_DAY',
    data: {
      outfit_of_the_day: {
        look_id: best.look.id,
        name: best.look.name || `Look #${String(best.look.id).slice(-4)}`,
        source: best.look.source || 'looks_ia',
        score: best.scoring.total,
        score_breakdown: best.scoring.breakdown,
        items: best.look.items || [],
        total_price: Math.round(best.scoring.price || 0),
        budget_status: best.scoring.budget_status || 'no_budget',
        badges: buildBadges(best.scoring),
        explanation: expl.explanation,
        reasons: expl.reasons,
      },
      alternatives,
      insights: buildInsights(ranked, prefs, context),
    },
    errors: [],
    warnings,
  };
}

// ─── HELPERS DE PRÉPARATION : OutfitEngine + userCreatedLooks → looks unifiés ─
/**
 * Convertit un outfit OutfitEngine ou un userCreatedLook en format attendu par
 * `getOutfitOfTheDay`. Calcule les style_tags / occasion_tags / season_tags
 * agrégés depuis les ai_metadata des items.
 */
export function normalizeLookForScoring(rawLook, source = 'looks_ia') {
  if (!rawLook) return null;
  const items = rawLook.items || [];

  const styleTags = new Set();
  const occTags = new Set();
  const seasonTags = new Set();
  for (const it of items) {
    const ai = it?.ai_metadata || {};
    if (ai.styleScores && typeof ai.styleScores === 'object') {
      for (const [k, v] of Object.entries(ai.styleScores)) {
        if ((v || 0) >= 60) styleTags.add(lc(k));
      }
    }
    if (Array.isArray(ai.styleTags)) for (const t of ai.styleTags) if (t) styleTags.add(lc(t));
    if (ai.occasions && typeof ai.occasions === 'object') {
      for (const [k, v] of Object.entries(ai.occasions)) {
        if ((v || 0) >= 60) occTags.add(lc(k));
      }
    }
    if (Array.isArray(ai.seasonality)) for (const s of ai.seasonality) if (s) seasonTags.add(lc(s));
  }
  if (rawLook.occasion) occTags.add(lc(rawLook.occasion));
  if (Array.isArray(rawLook.style_tags)) for (const t of rawLook.style_tags) styleTags.add(lc(t));

  return {
    id: rawLook.id || `${source}_${Math.random().toString(36).slice(2, 9)}`,
    name: rawLook.name || null,
    source,
    items: items.map(it => ({
      // v1.12.998 — préserver id + image(s) : sans eux, un consommateur qui rend
      // cette sortie affiche une case vide (article visible au Dressing mais pas
      // en Tenue du jour). On garde donc les champs visuels.
      id: it?.id,
      image: it?.image || it?.image_url || it?.photoUrl || it?.imageUrl || null,
      image_url: it?.image_url || it?.image || null,
      category: it?.category,
      subcategory: it?.subcategory,
      color: it?.color || it?.ai_metadata?.primaryColor,
      brand: it?.brand,
      price: it?.price,
      name: it?.name,
      material: it?.material,
      ai_metadata: it?.ai_metadata,
    })),
    style_tags: Array.from(styleTags),
    occasion_tags: Array.from(occTags),
    season_tags: Array.from(seasonTags),
    coherence_score: typeof rawLook.cohesion?.total === 'number'
      ? rawLook.cohesion.total
      : (typeof rawLook.coherence_score === 'number' ? rawLook.coherence_score : null),
    times_worn: rawLook.times_worn ?? 0,
    gender: rawLook.gender || null,
  };
}

// ─── v2 — BADGES (UI) ───────────────────────────────────────────────────
export function buildBadges(scoring) {
  const b = [];
  if (!scoring) return b;
  const total = scoring.total || 0;
  const bd = scoring.breakdown || {};
  const m = scoring.matches || {};
  if (total >= 280) b.push('⭐ PERFECT MATCH');
  else if (total >= 220) b.push('✨ STRONG MATCH');
  if (m.budget === 'great_value' || m.budget === 'good_value') b.push('💰 GOOD PRICE');
  // v1.12.559 — Badges SEASON FIT / WEATHER OK / FRESH supprimés à la demande user
  if (m.brand && m.brand.length > 0) b.push('🏷️ FAV BRAND');
  return b.slice(0, 3);
}

// ─── v2 — STYLIST_SELECTION (top N) ─────────────────────────────────────
export function stylistSelection(input) {
  const data = input?.data || {};
  const prefs = data.preferences || {};
  const context = data.context || {};
  const recentIds = Array.isArray(data.recently_worn_look_ids) ? data.recently_worn_look_ids : [];
  const looks = Array.isArray(data.available_looks) ? data.available_looks : [];
  const limit = Math.max(1, Math.min(50, Number(data.limit) || 10));
  const minScore = STYLIST_MIN_SCORE;

  if (looks.length === 0) {
    return { success: false, action: 'STYLIST_SELECTION', data: null,
      errors: [{ code: 'NO_LOOKS_AVAILABLE', message: 'Aucun look disponible.' }], warnings: [] };
  }

  const scored = looks.map(l => ({ look: l, scoring: scoreLook(l, prefs, context, recentIds) }));
  const eligible = scored.filter(x => !x.scoring.eliminated && (x.scoring.total || 0) >= minScore);
  eligible.sort((a, b) => b.scoring.total - a.scoring.total);

  const totalAnalyzed = scored.length;
  const totalEligible = eligible.length;
  const looksReturned = eligible.slice(0, limit);

  // budget_analysis
  const range = normalizeBudget(prefs);
  const eligiblePrices = eligible.map(x => x.scoring.price || 0).filter(p => p > 0);
  const budgetAnalysis = range && eligiblePrices.length > 0 ? {
    avg_price_eligible: Math.round(eligiblePrices.reduce((s, p) => s + p, 0) / eligiblePrices.length),
    min_price_eligible: Math.round(Math.min(...eligiblePrices)),
    max_price_eligible: Math.round(Math.max(...eligiblePrices)),
    looks_excluded_for_budget: scored.filter(x => x.scoring.reason === 'over_budget').length,
    recommendation: range.max > 100 ? 'Votre budget est bien calibré' : 'Augmentez votre budget pour plus de choix',
  } : null;

  // Insights
  const insights = [];
  if (totalEligible > 0) insights.push(`📈 ${totalEligible} look${totalEligible > 1 ? 's' : ''} correspondent à vos préférences`);
  if (budgetAnalysis) insights.push(`💰 Budget moyen recommandé : ${Math.round(budgetAnalysis.avg_price_eligible)}€`);
  if (budgetAnalysis?.looks_excluded_for_budget > 0) {
    insights.push(`⚠️ ${budgetAnalysis.looks_excluded_for_budget} look${budgetAnalysis.looks_excluded_for_budget > 1 ? 's' : ''} éliminé${budgetAnalysis.looks_excluded_for_budget > 1 ? 's' : ''} pour budget dépassé`);
  }
  // Couleurs dominantes
  const colorBag = new Map();
  for (const e of eligible.slice(0, 20)) {
    for (const c of dominantColors(e.look)) colorBag.set(c, (colorBag.get(c) || 0) + 1);
  }
  const topColors = Array.from(colorBag.entries()).sort((a,b) => b[1]-a[1]).slice(0, 3).map(([c]) => c);
  if (topColors.length > 0) insights.push(`🎨 Couleurs dominantes : ${topColors.join(', ')}`);

  // Warnings : budget vs marques luxe
  const warnings = [];
  if (range && range.max < 1000 && hasLuxuryBrandInPrefs(prefs)) {
    warnings.push('⚠️ Budget incompatible avec marques luxe sélectionnées (1500€+ recommandé)');
  }

  const selection = looksReturned.map(({ look, scoring }, idx) => {
    const expl = explainRecommendation(look, scoring, prefs, context);
    return {
      rank: idx + 1,
      look_id: look.id,
      name: look.name || `Look #${String(look.id).slice(-4)}`,
      source: look.source || 'looks_ia',
      score: scoring.total,
      score_breakdown: scoring.breakdown,
      items: look.items || [],
      total_price: Math.round(scoring.price || 0),
      budget_status: scoring.budget_status || 'no_budget',
      badges: buildBadges(scoring),
      reasons: expl.reasons,
    };
  });

  return {
    success: true,
    action: 'STYLIST_SELECTION',
    data: {
      feature: data.feature || 'stylist_selection',
      total_looks_analyzed: totalAnalyzed,
      looks_eligible: totalEligible,
      looks_returned: selection.length,
      selection,
      insights,
      budget_analysis: budgetAnalysis,
    },
    errors: [],
    warnings,
  };
}

// ─── v2 — FILTER_BY_BUDGET ──────────────────────────────────────────────
export function filterByBudget(looks, budget) {
  if (!Array.isArray(looks)) return [];
  if (!budget) return looks;
  const range = typeof budget === 'object' ? budget : { max: budget };
  const max = Number(range.max) || 99999;
  const flex = range.flexibility != null ? Number(range.flexibility) : 0.1;
  const hard = max * (1 + flex);
  return looks.filter(l => totalLookPrice(l) <= hard);
}

// ─── v2 — SUGGEST_BUDGET_ADJUSTMENT ─────────────────────────────────────
export function suggestBudgetAdjustment(prefs, looks = []) {
  const range = normalizeBudget(prefs);
  if (!range) return { needsBudget: true, message: 'Définissez un budget pour des recommandations adaptées' };
  if (hasLuxuryBrandInPrefs(prefs) && range.max < 1500) {
    return {
      needsAdjustment: true, current_max: range.max, recommended_min: 1500,
      reason: 'Marques de luxe sélectionnées nécessitent budget premium',
      message: '💡 Augmentez le budget OU enlevez les marques luxe pour rester réaliste',
    };
  }
  // Si <30 looks éligibles et budget bas
  const eligible = filterByBudget(looks, range);
  if (looks.length >= 50 && eligible.length < 10 && range.max < 200) {
    return {
      needsAdjustment: true, current_max: range.max, recommended_min: 250,
      reason: 'Peu de looks dans votre budget actuel',
      message: '💡 Élargissez à 250€+ pour plus de choix',
    };
  }
  return { needsAdjustment: false, message: 'Votre budget est bien calibré' };
}

// ─── v2 — GENERATE_AI_LOOK ──────────────────────────────────────────────
/**
 * Génère un look complet à partir d'un pool de produits du catalogue, contraint
 * par le budget. Stratégie greedy par catégorie : on choisit l'item qui maximise
 * (score IA × cohérence) sans exploser le budget restant.
 *
 * @param {Object} input — { preferences, context, available_products }
 * @returns { generated_look: { items, total_price, in_budget, explanation } }
 */
export function generateAILook(input) {
  const data = input?.data || input || {};
  const prefs = data.preferences || {};
  const context = data.context || {};
  const products = Array.isArray(data.available_products) ? data.available_products : [];
  const range = normalizeBudget(prefs) || { min: 0, max: 500, flexibility: 0.1 };
  const tempC = context?.weather?.temp_celsius ?? 18;

  if (products.length === 0) {
    return {
      success: false, action: 'GENERATE_AI_LOOK', data: null,
      errors: [{ code: 'NO_LOOKS_AVAILABLE', message: 'Catalogue vide' }],
    };
  }

  // Détermine la composition cible : robe seule OU haut+bas+chaussures
  const wantsDress = (prefs.categories || []).map(lc).some(c => c === 'dress' || c === 'robe' || c === 'robes');
  const composition = wantsDress
    ? ['Robes', 'Chaussures']
    : ['Hauts', 'Bas', 'Chaussures'];
  if (tempC < 12) composition.push('Manteaux');
  else if (tempC < 18) composition.push('Vestes');

  // Filtre par genre + couleur exclue + prix raisonnable (<= max par item)
  const exclColors = (prefs.excluded_colors || []).map(lc);
  const userColors = (prefs.colors || []).map(lc);
  const userStyles = (prefs.styles || []).map(lc);
  const maxPerItem = range.max; // simple cap : aucun item ne peut > max budget

  const eligible = products.filter(p => {
    if (!p || !p.price || p.price <= 0) return false;
    if (p.price > maxPerItem) return false;
    const c = lc(p.color || p.ai_metadata?.primaryColor);
    if (c && exclColors.some(ex => c.includes(ex))) return false;
    return true;
  });

  // Score un produit pour la composition voulue
  const scoreProduct = (p) => {
    let s = 0;
    const c = lc(p.color || p.ai_metadata?.primaryColor);
    if (userColors.length === 0 || userColors.some(uc => c.includes(uc) || uc.includes(c))) s += 20;
    const styles = (p.ai_metadata?.styleScores || {});
    for (const st of userStyles) if ((styles[st] || 0) >= 60) s += 15;
    if (p.ai_metadata?.isTrendy) s += 10;
    // v1.12.337 — partenaires premium / luxe Mytenue (boost selection styliste)
    if (p.partner === 'iqueens' || p.partner === 'cashmere'
        || p.partner === 'suit_negozi' || p.partner === 'ann_demeulemeester'
        || p.partner === 'coutr') s += 5;
    return s;
  };

  const generated = [];
  let remaining = range.max;
  for (const cat of composition) {
    const pool = eligible
      .filter(p => p.category === cat && p.price <= remaining)
      .sort((a, b) => scoreProduct(b) - scoreProduct(a));
    if (pool.length === 0) continue;
    const picked = pool[0];
    generated.push(picked);
    remaining -= picked.price;
  }

  const totalPrice = generated.reduce((s, it) => s + (it.price || 0), 0);
  const inBudget = totalPrice <= range.max * (1 + (range.flexibility || 0.1));
  const explanation = generated.length === composition.length
    ? `Look ${userStyles[0] || 'casual'} composé pour ${tempC}°C, total ${Math.round(totalPrice)}€ (budget ${range.max}€).`
    : `Look incomplet (${generated.length}/${composition.length} pièces) — pas assez d'items dans votre budget.`;

  return {
    success: generated.length === composition.length,
    action: 'GENERATE_AI_LOOK',
    data: {
      generated_look: {
        items: generated.map(p => ({
          category: p.category,
          subcategory: p.subcategory || null,
          product_id: p.id,
          name: p.name,
          brand: p.brand,
          price: p.price,
          image: p.image || p.image_url,
        })),
        total_price: Math.round(totalPrice),
        in_budget: inBudget,
        explanation,
      },
    },
    errors: generated.length === composition.length ? [] : [{ code: 'NO_GOOD_MATCH', message: explanation }],
  };
}

export const __TEST__ = {
  W, PENALTIES, MIN_ACCEPTABLE_SCORE,
  scoreStyle, scoreOccasion, scoreCategory, scoreWeather, scoreSeason,
  scoreColor, scoreBrand, scoreDiversity, scoreCoherence,
};
